import axios from 'axios';
import Swal from 'sweetalert2';
import { getTokenForDomain, storeToken } from './auth.utils';

// Normalised to exactly one trailing slash for the refreshToken call below, which
// concatenates `api/v1/eklink/...` onto it. Harmless as `baseURL`: axios collapses the
// separator when it joins a relative path. See the note in services/api.service.js.
const authApiUrl = `${(process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')}/`;

let authToken = getTokenForDomain('accessToken');

const axiosInstance = axios.create({
  baseURL: authApiUrl,
  headers: {
    Authorization: authToken ? `Bearer ${authToken}` : undefined,
  },
});

// Request Interceptor
axiosInstance.interceptors.request.use(
  (req) => {
    if (!authToken) {
      authToken = getTokenForDomain('accessToken');
      if (authToken) {
        req.headers.Authorization = `Bearer ${authToken}`;
      }
    }
    return req;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      try {
        const refreshResponse = await refreshTokenAndRetry(error);
        return refreshResponse;
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    } else {
      // For error responses, include ALL data from the backend response
      // This is critical for handling special cases like 3DS (402) which need clientSecret
      const result = {
        statusCode: error.response?.data?.statusCode || error.response?.status || 500,
        message: error.response?.data?.message || 'Unknown error',
      };

      // Include all other fields from the backend response
      if (error.response?.data) {
        Object.keys(error.response.data).forEach((key) => {
          if (key !== 'statusCode' && key !== 'message') {
            result[key] = error.response.data[key];
          }
        });
      }

      console.log('Axios interceptor error response:', result);
      return Promise.reject(result);
    }
  }
);

async function refreshTokenAndRetry(error) {
  try {
    const response = await refreshToken(error);
    return response;
  } catch (refreshError) {
    return Promise.reject(refreshError);
  }
}

async function refreshToken(error) {
  try {
    const refreshTokenValue = getTokenForDomain('refreshToken');
    const response = await axios.post(`${authApiUrl}api/v1/eklink/user/auth/refreshToken`, { data: { "refreshToken": refreshTokenValue } });
    if (response.data.statusCode === 200 || response.data.statusCode === 201) {
      const { accessToken, refreshToken } = response.data.data;
      const domain = localStorage.getItem('tokenDomain') || window.location.hostname;

      // A refresh that comes back without tokens leaves nothing to retry with — treat it as an
      // expired session rather than storing "undefined" and replaying it on every request.
      if (!storeToken("accessToken", accessToken, domain)) {
        handleSessionExpired();
        return;
      }
      storeToken("refreshToken", refreshToken, domain);

      authToken = accessToken;
      error.response.config.headers['Authorization'] = `Bearer ${accessToken}`;
      return axiosInstance.request(error.response.config);
    } else {
      if (response.data.statusCode === 401) {
        handleSessionExpired();
      }
    }
  } catch (err) {
    handleSessionExpired();
  }
}


function handleSessionExpired() {
  // Clear all token-related localStorage items
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken_domain");
  localStorage.removeItem("accessToken_domain");
  localStorage.removeItem("tokenDomain");
  
  Swal.fire({
    text: 'Session Expired',
    confirmButtonText: 'Ok',
    customClass: {
      popup: 'customSwal',
    },
  }).then((result) => {
    if (result.isConfirmed) {
      window.location.href = '/auth/login';
    }
  });
  throw new Error('Session expired');
}

export default axiosInstance;
